import { useEffect, useRef, useState } from 'react';

import { useStartHotelCardSetupMutation } from 'generated/graphql';

import { preloadHotelCardStripe } from './hotelCardStripe';
import { notifyHotelSavedCardsChanged } from './savedCardReturn';

type SetupState = {
  identity: string;
  attempt: number;
  phase: 'starting' | 'entry' | 'syncing' | 'saved-error' | 'error';
  clientSecret?: string;
  error?: string;
};

/** Keep retries and late Stripe responses scoped to the current actor/client. */
export function useHotelCardSetup(
  identity: string,
  onSaved: () => Promise<unknown>,
) {
  const [startSetup] = useStartHotelCardSetupMutation();
  const [state, setState] = useState<SetupState | null>(null);
  const returnFocus = useRef<HTMLButtonElement>(null);
  const requestKey = useRef(crypto.randomUUID());
  const current = useRef(identity);
  current.current = identity;
  const mounted = useRef(true);
  const attempt = useRef(0);
  const pending = useRef(false);
  useEffect(() => {
    if (identity) preloadHotelCardStripe();
  }, [identity]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    requestKey.current = crypto.randomUUID();
    attempt.current++;
    pending.current = false;
    setState(null);
  }, [identity]);
  const active = state?.identity === identity ? state : null;
  const isCurrent = () =>
    !!active &&
    mounted.current &&
    current.current === active.identity &&
    attempt.current === active.attempt;

  async function addCard() {
    if (pending.current) return;
    const submittedIdentity = identity;
    const submittedAttempt = ++attempt.current;
    const isCurrent = () =>
      mounted.current &&
      current.current === submittedIdentity &&
      attempt.current === submittedAttempt;
    pending.current = true;
    setState({ identity, attempt: submittedAttempt, phase: 'starting' });
    try {
      if (
        !/^pk_(test|live)_/.test(
          import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? '',
        )
      )
        throw new Error('Card setup unavailable');
      preloadHotelCardStripe();
      const result = await startSetup({
        variables: { requestKey: requestKey.current },
      });
      if (!isCurrent()) return;
      const clientSecret = result.data?.startHotelCardSetup.clientSecret;
      if (!clientSecret) throw new Error('Card setup unavailable');
      requestKey.current = crypto.randomUUID();
      setState({
        identity,
        attempt: submittedAttempt,
        phase: 'entry',
        clientSecret,
      });
    } catch (error) {
      if (!isCurrent()) return;
      if (
        (
          error as { graphQLErrors?: Array<{ extensions?: { code?: string } }> }
        ).graphQLErrors?.some(
          (item) => item.extensions?.code === 'HOTEL_CARD_SETUP_RESTART',
        )
      )
        requestKey.current = crypto.randomUUID();
      setState({
        identity,
        attempt: submittedAttempt,
        phase: 'error',
        error: 'Unable to load the card form. Please try again.',
      });
    } finally {
      if (isCurrent()) pending.current = false;
    }
  }

  async function complete() {
    if (!active || pending.current) return;
    const submitted = active;
    const isCurrent = () =>
      mounted.current &&
      current.current === submitted.identity &&
      attempt.current === submitted.attempt;
    pending.current = true;
    setState({ ...submitted, phase: 'syncing', error: undefined });
    try {
      await onSaved();
      if (!isCurrent()) return;
      notifyHotelSavedCardsChanged();
      setState(null);
    } catch {
      if (isCurrent())
        setState({
          ...submitted,
          phase: 'saved-error',
          error:
            'Your card was saved, but the card list could not be updated. Please try again.',
        });
    } finally {
      if (isCurrent()) pending.current = false;
    }
  }

  function close() {
    attempt.current++;
    pending.current = false;
    setState(null);
  }
  function formError() {
    if (active && isCurrent())
      setState({
        ...active,
        phase: 'error',
        error: 'Unable to load the card form. Please try again.',
      });
  }
  return {
    addCard,
    close,
    complete,
    formError,
    isCurrent,
    state: active,
    returnFocus,
    loading: active?.phase === 'starting',
  };
}
export type HotelCardSetup = ReturnType<typeof useHotelCardSetup>;
